import Helper from "../../../../Common/Helper";
import { appContext } from "../../../../AppContext";
import { RouteComponentProps } from "react-router";
import { INode, PagedResponse } from "../../../../Providers.Api/Models";
import { DateTime } from "luxon";
import { Box, Grid, SelectChangeEvent, Typography } from '@mui/material'
import IbssButton from '../../../../Components/Buttons/Button/IbssButton'
import EditVisitorStatus from './EditVisitorStatus';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import Spinner from "../../../../Components/Navigation/LoadingSpinner/Spinner";
import AssociatedDetails from './AssociatedDetails';
import { DateHelper } from '../../../../Common/DateHelper';
import { VisitDetail, QuestionType, ICheckInWorkflow, ICheckInWorkflowRaw, Space } from './DataModels';
import IbssDialog from '../../../../Components/Dialogs/BaseDialog/IbssDialog';
import IbssButtonRedo from "../../../../Components/Buttons/Button/IbssButton";
import Notes, { INote } from "../../../../Components/Lists/Notes/Notes";
import { Diff, IDiffResult } from "../../../../Common/Diff";
import { Note } from "../../../OneLens/Visits/ViewDashboard/DataModels";
import { EventType } from "../../../../Providers.Api/EventTypes";
import { ApiConstants } from "../../../../Providers.Api/ApiConstants";
import ConfirmModal from "../../../../Components/Dialogs/ConfirmDialog/ConfirmModal";
import "../../../../styles/css/dashboard.scss";
import "../../../../styles/css/header.scss";
import "../../../../App.css";
import { QueryParams } from "./QueryParams";
import EditManagement, { IQuestion } from "./EditManagement";
import './Styles.scss';
import { IVisitorChange, Visitor } from "../../../../Components/Forms/Visitor/Visitor";
import { ISelectOption } from "../../../../Components/Inputs/SelectList/IbssInputDropDown";
import { IPutVisitV2 } from "../../../../Providers.IbssApiClientV2/IbssApiClientV2";
import { IUser } from "../../../../Components/Inputs/UserPicker/UserPicker";
import { rights } from "../../../../Common/RightsManager";

class EditVisit extends IbssPage<IProps, IState, QueryParams>
{
    private get labels() { return appContext().labels; }
    private get alert() { return appContext().alert; }
    private get local() { return appContext().localStorageProvider; }
    private get hasV2Rights() { return rights.API.Visits.V2.has(); }
    private get hasUpdateRights() { return rights.DATAMODEL.Visits.Update.has(); }
    private get hasDeleteRights() { return rights.DATAMODEL.Visits.Delete.has(); }
    private hasApproveRights(buildingId: number) { return rights.API.Visits.Approve.has(buildingId); }
    private hasDenyRights(buildingId: number) { return rights.API.Visits.Deny.has(buildingId); }
    private hasCheckinRights(buildingId: number) { return rights.API.Visits.CheckIn.has(buildingId); }
    private hasCheckoutRights(buildingId: number) { return rights.API.Visits.CheckOut.has(buildingId); }
    private currentUser = appContext().localStorageProvider.getUserDetails();
    private buildingId = 1;
    private visitId = '';
    private visitData?: VisitDetail;

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.state =
        {
            isValid: false,
            isLoading: false,
            firstName: "",
            lastName: "",
            company: "",
            email: "",
            visitorTypeId: "",
            pacs: "",
            hostEmail: "",
            host: null,
            arrivalLocation: "",
            arrivalLocationId: "",
            bookingName: "",
            bookingId: "",
            bookingStart: DateTime.now(),
            bookingEnd: DateTime.now(),
            isApproved: 0,
            isCancelled: 0,
            isDenied: 0,
            isCheckedIn: 0,
            isCheckedOut: 0,
            isNoShow: 0,
            status: "",
            visitorStartDate: DateTime.now(),
            visitorEndDate: DateTime.now(),
            approveButtonDisabled: true,
            denyButtonDisabled: true,
            checkInButtonDisabled: true,
            checkOutButtonDisabled: true,
            showDenyModal: false,
            denyComment: "",
            originalNotes: [],
            notes: [],
            arrivalLocations: [],
            showDeleteConfirmationModal: false,
            buildingVisitorArrivalWindow: 0,
            checkInQuestions: [],
        }
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        if (!this.queryParams.building)
        {
            return this.pushQueryParams({ building: 1 }, true);
        }
        this.buildingId = this.queryParams.building;
        this.visitId = this.props.match.params.visit_Id;
        const buildingName = Helper.getBuildingNameUsingBuildingId(this.buildingId);
        this.pageTitle = this.labels.HubMenuMyVisitors + ' - ' + buildingName;
        this.setBuildingVisitorArrivalWindow();
        await this.loadVisitorDetails();
        await this.loadLocationDetails();
        await this.loadNotes();
    }

    private setBuildingVisitorArrivalWindow(): void
    {
        const building = Helper.getAllBuildingsData().find(building => building.Node_Id == this.buildingId) as INode;
        this.setState({ buildingVisitorArrivalWindow: building.Vis_Arvl_Wndw_Mins });
    }

    private async loadVisitorDetails(): Promise<void>
    {
        this.setState({ isLoading: true });
        const visit = this.visitData = await this.getVisitorDetails(this.buildingId, this.visitId, this.hasV2Rights);
        const currentTime = DateHelper.now(this.buildingId);
        const earlyCheckInTime = visit.Visit_Start_Date.minus({ minutes: this.state.buildingVisitorArrivalWindow });
        const isInVisitWindow = (currentTime >= earlyCheckInTime && currentTime <= visit.Visit_End_Date);

        const checkInQuestions = visit.CheckInWorkflow
            .Steps
            .flatMap(i => i.Questions)
            .map((questionData, questionIndex) =>
            {
                const answer = questionData.Answers[0] ?? '';
                return {
                    questionIdOrIndex: questionData.Id || questionIndex,
                    type: questionData.InputType,
                    value: questionData.Value || questionData.Name || questionData.QuestionName,
                    policyWording: questionData.PolicyText,
                    policyConfirmationText: questionData.ConfirmationText,
                    placeHolderText: questionData.PlaceHolderText,
                    helperText: questionData.HelperText,
                    options: questionData.Labels.map(i => i.Label),
                    height: questionData.InputHeight,
                    maxLength: questionData.MaxLength,
                    answer: answer?.Value ?? '',
                    answeredAt: answer?.ModifiedAt ?? DateHelper.null(),
                    answeredBy: answer?.ModifiedBy ?? '',
                } as IQuestion
            });

        this.setState({
            firstName: visit.Visitor_First_Name,
            lastName: visit.Visitor_Last_Name,
            company: visit.Visitor_Company,
            email: visit.Visitor_Email,
            visitorTypeId: visit.VisitorTypeId,
            pacs: visit.Visit_Pacs_Id,
            hostEmail: visit.Visit_Host_Email,
            arrivalLocation: visit.Space_Name,
            arrivalLocationId: visit.Space_Id,
            bookingName: visit.Booking_Name,
            bookingId: visit.Booking_Id,
            bookingStart: visit.Booking_Start,
            bookingEnd: visit.Booking_End,
            isApproved: visit.Visit_IsApproved,
            isCancelled: visit.Visit_IsCancelled,
            isDenied: visit.Visit_IsDenied,
            isCheckedIn: visit.Visit_IsCheckedIn,
            isCheckedOut: visit.Visit_IsCheckedOut,
            isNoShow: visit.Visit_IsNoShow,
            status: visit.Visit_Status,
            visitorStartDate: visit.Visit_Start_Date,
            visitorEndDate: visit.Visit_End_Date,
            isLoading: false,
            approveButtonDisabled: visit.Visit_Status != "Awaiting Approval" || !this.hasApproveRights(this.buildingId),
            denyButtonDisabled: visit.Visit_Status != "Awaiting Approval" || !this.hasDenyRights(this.buildingId),
            checkInButtonDisabled: visit.Visit_Status != "Approved" || !this.hasCheckinRights(this.buildingId) || !isInVisitWindow,
            checkOutButtonDisabled: visit.Visit_Status != "Checked In" || !this.hasCheckoutRights(this.buildingId),
            checkInQuestions: checkInQuestions,
        });
    }

    private async getVisitorDetails(nodeId: number, visitId: string, hasV2Rights: boolean): Promise<VisitDetail>
    {
        if (hasV2Rights)
        {
            return await appContext().ibssApiClientV2.v2.byNodeid.visits.byVisitid.get<VisitDetail>({
                nodeId,
                visitid: visitId,
                select: VisitDetail,
            });
        }
        else
        {
            const visit = await appContext().ibssApiClientV1.v1.byNodeid.visits.byVisitid.get<VisitDetail>({
                nodeId,
                visitid: visitId,
            });
            const typedVisit = new VisitDetail().init(visit, this.buildingId);
            return typedVisit;
        }
    }


    private async loadLocationDetails(): Promise<void>
    {
        try 
        {
            this.setState({ isLoading: true });
            const response = await appContext().ibssApiClientV2.v2.byNodeid.spaces.get<PagedResponse<Space[]>>({
                nodeId: this.buildingId,
                filter: "Space_Arrival_Loc eq 1",
                select: Space,
            });

            const arrivalLocations: ISelectOption<string, string>[] = response.value.map(space => ({ label: space.Space_Name, value: space.Space_Id }));
            const { arrivalLocation, arrivalLocationId } = this.state;
            this.setState({ arrivalLocations: [{ label: arrivalLocation, value: arrivalLocationId }, ...arrivalLocations.filter(space => space.value !== arrivalLocationId)] });
            const selectedLocation = arrivalLocations.find((item) => item.value === this.state.arrivalLocationId);

            if (selectedLocation)
            {
                this.setState({
                    arrivalLocation: selectedLocation.label,
                    arrivalLocationId: selectedLocation.value,
                });
            }
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async loadNotes(): Promise<void>
    {
        const notesData = await appContext().ibssApiClientV2.v2.byNodeid.notes.get<PagedResponse<Note[]>>({
            nodeId: this.buildingId,
            filter: `EventType_Id eq ${EventType.visits.id} and Event_Id eq '${this.visitId}' and IsDeleted eq 0`,
            select: Note,
            top: ApiConstants.largeODataTop,
        });

        const notes: INote[] = notesData.value.map(i => ({
            isNew: false,
            id: i.Note_Id,
            value: i.Note,
            author: i.CreatedBy,
            time: i.CreatedAt,
        }));

        this.setState({
            originalNotes: JSON.parse(JSON.stringify(notes)),
            notes: notes,
        })
    }

    private handleVisitorChange(props: IVisitorChange): void
    {
        if (props.firstName != undefined) { this.setState({ firstName: props.firstName }); }
        if (props.lastName != undefined) { this.setState({ lastName: props.lastName }); }
        if (props.email != undefined) { this.setState({ email: props.email }); }
        if (props.visitorTypeId != undefined) { this.setState({ visitorTypeId: props.visitorTypeId }); }
        if (props.company != undefined) { this.setState({ company: props.company }); }
        if (props.pacs != undefined) { this.setState({ pacs: props.pacs }); }
        if (props.notes != undefined) { this.setState({ notes: props.notes }); }
        if (props.hostEmail != undefined) { this.setState({ hostEmail: props.hostEmail }); }
        if (props.host != undefined) { this.setState({ host: props.host }); }
        if (props.visitorStartDate != undefined) { this.setState({ visitorStartDate: props.visitorStartDate }); }
        if (props.visitorEndDate != undefined) { this.setState({ visitorEndDate: props.visitorEndDate }); }

        if (props.arrivalLocationId != undefined)
        {
            const arrivalLocation = this.state.arrivalLocations.find(i => i.value === props.arrivalLocationId);
            if (arrivalLocation)
            {
                this.setState({ arrivalLocation: arrivalLocation.label, arrivalLocationId: arrivalLocation.value });
            }
        }
    }

    private handleVisitorValidate(isValid: boolean): void
    {
        this.setState({ isValid: isValid });
    }

    private async handleSave(): Promise<void>
    {
        const diff = this.notesDiff;
        try 
        {
            this.setState({ isLoading: true });
            const currentDateTime = DateTime.now().toUTC();
            const visitStartDateTime = this.state.visitorStartDate.toUtcByNode(this.buildingId);

            if (this.visitData && visitStartDateTime > currentDateTime) 
            {
                this.visitData.CheckInWorkflowRaw.Steps.flatMap(i => i.Questions).map((questionData, questionIndex) =>
                {
                    const question = this.state.checkInQuestions.find(i => i.questionIdOrIndex == (questionData.Id || questionIndex));
                    if (question && question.answeredAt.isNull())
                    {
                        questionData.Answers = [];
                    }
                    else if (question)
                    {
                        questionData.Answers = [{
                            Value: question.answer,
                            ModifiedAt: question.answeredAt.toUtcByNode(this.buildingId).toISO(),
                            ModifiedBy: question.answeredBy,
                        }];
                    }
                });

                const payload: (IPutVisitV2 & { VisitorTypeWorkflow: ICheckInWorkflowRaw }) =
                {
                    Visitor_First_Name: this.state.firstName,
                    Visitor_Last_Name: this.state.lastName,
                    Visitor_Email: this.state.email,
                    Visitor_Company: this.state.company,
                    Visit_Host_Name: this.state.host?.displayName ?? '',
                    Visit_Host_Email: this.state.host?.email ?? '',
                    Visit_Start_Date: visitStartDateTime.toISO(),
                    Visit_End_Date: this.state.visitorEndDate.toUtcByNode(this.buildingId).toISO(),
                    Visit_Approval_Comments: "",
                    Visitor_Dietary_Pref: 0,
                    Visit_Pacs_Id: this.state.pacs,
                    Visit_Save_Info: 0,
                    Space_Name: this.state.arrivalLocation,
                    Space_Id: this.state.arrivalLocationId,
                    VisitorTypeWorkflow: this.visitData.CheckInWorkflowRaw,
                    VisitorTypeId: this.state.visitorTypeId === 'None' ? null : this.state.visitorTypeId,
                };
                this.saveNotes();
                const data = await appContext().ibssApiClientV2.v2.byNodeid.visits.byVisitid.put({ nodeId: this.buildingId, visitid: this.visitId, body: payload, disableExtUpdate: false });
                if (data)
                {
                    this.setState({ isLoading: false });
                    this.navigateToListVisits();
                }
            }
            else if (diff.createdItems.length > 0 || diff.updatedItemsAfter.length > 0 || diff.deletedItems.length > 0)
            {
                this.saveNotes();
                this.alert.show(this.labels.HubLabelWarning, this.labels.funcVisitorRecordPast_D);
            }
            else 
            {
                this.navigateToListVisits();
            }
        }
        catch (error) 
        {
            this.saveNotes();
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private get notesDiff(): IDiffResult<INote>
    {
        const { originalNotes, notes } = this.state;
        const diff = new Diff(originalNotes, notes, i => i.id, (a, b) => a.value == b.value);
        const result = diff.execute();
        return result;
    }

    private async saveNotes(): Promise<void>
    {
        const diff = this.notesDiff;
        for (let note of diff.createdItems)
        {
            try
            {
                appContext().ibssApiClientV2.v2.byNodeid.notes.post({
                    nodeId: this.buildingId,
                    body: {
                        Node_Id: this.buildingId,
                        EventType_Id: EventType.visits.id,
                        EventType_Name: EventType.visits.name,
                        Event_Id: this.visitId,
                        Note: note.value,
                        IsDeleted: 0
                    },
                });
            }
            catch { }
        }

        for (let note of diff.updatedItemsAfter)
        {
            try
            {
                appContext().ibssApiClientV2.v2.byNodeid.notes.byNoteid.put({
                    nodeId: this.buildingId,
                    noteid: note.id,
                    body: {
                        Node_Id: this.buildingId,
                        Note_Id: note.id,
                        EventType_Id: EventType.visits.id,
                        EventType_Name: EventType.visits.name,
                        Event_Id: this.visitId,
                        Note: note.value,
                        IsDeleted: 0,
                    },
                });
            }
            catch { }
        }

        for (let note of diff.deletedItems)
        {
            try
            {
                appContext().ibssApiClientV2.v2.byNodeid.notes.byNoteid.cancel.patch({
                    nodeId: this.buildingId,
                    noteid: note.id,
                    body: {
                        IsDeleted: 1
                    },
                });
            }
            catch { }
        }
    }

    private async handleDelete(): Promise<void>
    {
        try 
        {
            const data: string = await appContext().ibssApiClientV2.v2.byNodeid.visits.byVisitid.delete({ nodeId: this.buildingId, visitid: this.visitId })
            this.alert.show("", data);
        }
        catch (error) 
        {
        }
        this.navigateToListVisits();
    }

    private navigateToListVisits(): void
    {
        this.props.history.push(this.area == 'flex' ? '/flex/visits' : `/one-lens/visits?building=${this.buildingId}`);
    }

    private async handleApprove(): Promise<void>
    {
        this.setState({ isLoading: true });
        try
        {
            await appContext().ibssApiClientV2.v2.byNodeid.visits.approve.post({
                nodeId: this.buildingId,
                body: [{
                    Visit_Id: this.visitId,
                    Visit_Approve_Comment: ''
                }]
            });
            await this.loadVisitorDetails();
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async handleDeny(): Promise<void>
    {
        this.setState({ isLoading: true });
        try
        {
            await appContext().ibssApiClientV2.v2.byNodeid.visits.deny.post({
                nodeId: this.buildingId,
                body: [{
                    Visit_Id: this.visitId,
                    Visit_Denial_Comment: this.state.denyComment
                }]
            });
            await this.loadVisitorDetails();
        }
        finally
        {
            this.setState({ showDenyModal: false, isLoading: false });
        }
    }

    private async handleCheckIn(): Promise<void>
    {
        this.setState({ isLoading: true });
        try
        {
            if (this.visitData) 
            {
                this.visitData.CheckInWorkflowRaw.Steps.flatMap(i => i.Questions).forEach((questionData, questionIndex) =>
                {
                    const question = this.state.checkInQuestions.find(i => i.questionIdOrIndex == (questionData.Id || questionIndex));
                    if (question && question.answeredAt.isNull())
                    {
                        questionData.Answers = [];
                    }
                    else if (question)
                    {
                        questionData.Answers = [{
                            Value: question.answer,
                            ModifiedAt: question.answeredAt.toUtcByNode(this.buildingId).toISO(),
                            ModifiedBy: question.answeredBy,
                        }];
                    }
                });
            }
            await appContext().ibssApiClientV2.v2.byNodeid.visits.checkin.post({
                nodeId: this.buildingId, body: [{
                    Visit_Id: this.visitId,
                    VisitorTypeWorkflow: this.visitData?.CheckInWorkflowRaw
                }]
            });
            await this.loadVisitorDetails();
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async handleCheckOut(): Promise<void>
    {
        this.setState({ isLoading: true });
        try
        {
            await appContext().ibssApiClientV2.v2.byNodeid.visits.checkout.post({
                nodeId: this.buildingId, body: [{
                    Visit_Id: this.visitId,
                }]
            });
            await this.loadVisitorDetails();
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private handleAnswerDelete(questionIdOrIndex: string | number): void
    {
        const question = this.state.checkInQuestions.find(i => i.questionIdOrIndex == questionIdOrIndex);
        if (!question)
        {
            return;
        }
        question.answer = '';
        question.answeredAt = DateHelper.null();
        question.answeredBy = '';
        this.setState({ checkInQuestions: this.state.checkInQuestions });
    }

    private handleAnswerSubmit(questionIdOrIndex: string | number, answer: string): void
    {
        const question = this.state.checkInQuestions.find(i => i.questionIdOrIndex == questionIdOrIndex);
        if (!question)
        {
            return;
        }
        question.answer = answer;
        question.answeredAt = DateHelper.now(this.buildingId);
        question.answeredBy = this.currentUser.email;
        this.setState({ checkInQuestions: this.state.checkInQuestions });
    }

    public render(): JSX.Element
    {
        const isEndTimeValid = this.state.visitorEndDate > DateHelper.now(this.buildingId)
        const canSubmit = (this.state.isValid && this.hasUpdateRights);
        const disableFields = this.state.status === "Checked Out" || this.state.status === "Cancelled" || this.state.status === "No Show";

        return (
            <div className="page-height-exct-header">
                <div className="rightPanel-main-content">
                    <h2>{this.labels.HubLabelvisitor} : {this.state.firstName} {this.state.lastName}</h2>
                    {this.state.isLoading && <Spinner />}
                    <IbssDialog
                        aria-modal="true"
                        aria-label="deny modal"
                        onClose={() => this.setState({ showDenyModal: !this.state.showDenyModal })}
                        open={this.state.showDenyModal}
                        header={this.labels.HubButtonDeny}
                        fullWidth
                        dialogContent={
                            <>
                                <div className="col-12">
                                    <label>{this.labels.HubLabelComment}</label>
                                    <input className="input-box border model-textbox" type="text" name="denyComment" value={this.state.denyComment} onChange={(e) => this.setState({ denyComment: e.target.value })} />
                                </div>
                            </>
                        }
                        footer={
                            <>
                                <IbssButtonRedo
                                    color="secondary"
                                    variant="outlined"
                                    className="mr-2"
                                    onClick={() => this.setState({ showDenyModal: !this.state.showDenyModal })}>
                                    {this.labels.HubButtonCancel}
                                </IbssButtonRedo>
                                <IbssButtonRedo
                                    color="primary"
                                    variant="contained"
                                    size="medium"
                                    disabled={!this.state.denyComment}
                                    onClick={() => this.handleDeny()}>
                                    {this.labels.HubLabelOk}
                                </IbssButtonRedo>
                            </>
                        }
                    />
                    <ConfirmModal
                        show={this.state.showDeleteConfirmationModal}
                        modalHeading={this.labels.HubLabelDeleteCol}
                        handleModal={() => this.setState({ showDeleteConfirmationModal: false })}
                        modalMessage={this.labels.HubLabelDeleteMessage}
                        okButton={() => this.handleDelete()}
                    />
                    <Grid container >
                        <Grid xs={6} pr={2}>
                            <Visitor
                                buildingId={this.buildingId}
                                firstName={this.state.firstName}
                                lastName={this.state.lastName}
                                company={this.state.company}
                                email={this.state.email}
                                visitorTypeId={this.state.visitorTypeId}
                                onLoading={() => this.setState({ isLoading: true })}
                                onLoaded={() => this.setState({ isLoading: false })}
                                pacs={this.state.pacs}
                                notes={this.state.notes}
                                hostEmail={this.state.hostEmail}
                                visitorStartDate={this.state.visitorStartDate}
                                visitorEndDate={this.state.visitorEndDate}
                                arrivalLocationId={this.state.arrivalLocationId}
                                arrivalLocations={this.state.arrivalLocations}
                                onChange={props => this.handleVisitorChange(props)}
                                onValidate={isValid => this.handleVisitorValidate(isValid)}
                                disableFields={disableFields}
                            />
                        </Grid>
                        <Grid xs={6}>
                            <Box className="tablePanel">
                                {this.area == 'onelens' &&
                                    <EditManagement
                                        questions={this.state.checkInQuestions}
                                        onDelete={(questionIdOrIndex) => this.handleAnswerDelete(questionIdOrIndex)}
                                        onSubmit={(questionIdOrIndex, answer) => this.handleAnswerSubmit(questionIdOrIndex, answer)}
                                    />
                                }
                                <EditVisitorStatus
                                    visitId={this.visitId}
                                    status={this.state.status}
                                    visitData={this.visitData}
                                />
                                <Box display={'flex'} justifyContent={'space-between'} mx={2}>
                                    <IbssButton color="secondary" variant="contained" size="medium" disabled={this.state.approveButtonDisabled || !this.hasUpdateRights} onClick={() => this.handleApprove()}>
                                        {this.labels.HubLabelApprove}
                                    </IbssButton>
                                    <IbssButton color="secondary" variant="contained" size="medium" disabled={this.state.denyButtonDisabled || !this.hasUpdateRights} onClick={() => this.setState({ showDenyModal: !this.state.showDenyModal })}>
                                        {this.labels.HubButtonDeny}
                                    </IbssButton>
                                    <IbssButton color="secondary" variant="contained" size="medium" disabled={this.state.checkInButtonDisabled || !this.hasUpdateRights || !isEndTimeValid} onClick={() => this.handleCheckIn()}>
                                        {this.labels.HubButtonCheckIn}
                                    </IbssButton>
                                    <IbssButton color="secondary" variant="contained" size="medium" disabled={this.state.checkOutButtonDisabled || !this.hasUpdateRights} onClick={() => this.handleCheckOut()}>
                                        {this.labels.HubButtonCheckOut}
                                    </IbssButton>
                                </Box>
                                {this.state.bookingId &&
                                    <AssociatedDetails
                                        arrivalLocation={this.state.arrivalLocation}
                                        arrivalLocationId={this.state.arrivalLocationId}
                                        bookingName={this.state.bookingName}
                                        bookingId={this.state.bookingId}
                                        bookingStart={this.state.bookingStart}
                                        bookingEnd={this.state.bookingEnd}
                                    />
                                }
                            </Box>
                        </Grid>
                    </Grid>
                    <Box className="rightPanel-main-button-group">
                        <Box>
                            <IbssButton color='error' disabled={!this.hasDeleteRights} variant="contained" size="medium" onClick={() => this.setState({ showDeleteConfirmationModal: true })}>
                                {this.labels.funcCateringMenuEditDelete_S}
                            </IbssButton>
                        </Box>
                        <Box>
                            <IbssButton color="secondary" className="mr-2" variant="contained" size="medium" onClick={() => this.navigateToListVisits()}>
                                {this.labels.HubButtonCancel}
                            </IbssButton>
                            <IbssButton
                                disabled={!canSubmit || disableFields}
                                color="primary"
                                variant="contained"
                                size="medium"
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.HubButtonSave}
                            </IbssButton>
                        </Box>
                    </Box>
                </div>
            </div>
        )
    }
}

export default EditVisit;

export interface IQueryParams
{
    buildingid: string;
    visit_Id: string;
}

export interface IProps extends RouteComponentProps<IQueryParams>
{
}


export interface IVisitorDetail
{
    visitorFirstName: string;
    visitorLastName: string;
    company: string;
    visitorEmail: string;
    pacsId: string;
}

export interface IState
{
    isValid: boolean;
    isLoading: boolean;
    firstName: string;
    lastName: string;
    company: string;
    email: string;
    visitorTypeId: string;
    pacs: string;
    hostEmail: string;
    host: IUser | null;
    isApproved: number;
    isCancelled: number;
    isDenied: number;
    isCheckedIn: number;
    isCheckedOut: number;
    isNoShow: number;
    status: string;
    arrivalLocation: string;
    arrivalLocationId: string;
    bookingName: string;
    bookingId: string;
    bookingStart: DateTime;
    bookingEnd: DateTime;
    visitorStartDate: DateTime;
    visitorEndDate: DateTime;
    showDenyModal: boolean;
    denyComment: string,
    approveButtonDisabled: boolean;
    denyButtonDisabled: boolean;
    checkInButtonDisabled: boolean;
    checkOutButtonDisabled: boolean;
    originalNotes: INote[];
    notes: INote[];
    arrivalLocations: ISelectOption<string, string>[];
    showDeleteConfirmationModal: boolean;
    buildingVisitorArrivalWindow: number;
    checkInQuestions: IQuestion[];
}
